import { ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useRouter } from '@/@core/utils/utils'
import store from '@/store'
import { postData } from '@/@core/utils/http/general'

const DASHBOARD_ROUTE = '/aci/dashboard'

const FILE_CSV_COLUMN = {
  1: 'enlace',
  2: 'nombre',
  3: 'segundoNombre',
  4: 'apellidoPaterno',
  5: 'apellidoMaterno',
  6: 'correo',
  7: 'correoAlt',
  8: 'telefono1',
  9: 'telefono2',
  10: 'extension',
  11: 'curp',
  12: 'nacionalidad',
  13: 'tipoGobierno',
  14: 'entidadFederativa',
  15: 'municipio',
  16: 'dependencia',
  17: 'puesto',
  18: 'grado',
  19: 'departamento',
  20: 'carrera',
  21: 'cedula',
  22: 'efTitulacion',
  23: 'instEducativa',
  24: 'cct',
  25: 'fechaExpCedula',
}

const TABLE_FIELDS = [
  {
    key: 'enlace', label: 'Enlace', stickyColumn: true, isRowHeader: true,
  },
  { key: 'nombre', label: 'Nombre' },
  { key: 'segundoNombre', label: 'Segundo nombre' },
  { key: 'apellidoPaterno', label: 'Apellido paterno' },
  { key: 'apellidoMaterno', label: 'Apellido materno' },
  { key: 'correo', label: 'Correo electrónico' },
  { key: 'correoAlt', label: 'Correo alternativo' },
  { key: 'telefono1', label: 'Teléfono 1' },
  { key: 'telefono2', label: 'Teléfono 2' },
  { key: 'extension', label: 'Extensión' },
  { key: 'curp', label: 'CURP' },
  { key: 'nacionalidad', label: 'Nacionalidad' },
  { key: 'tipoGobierno', label: 'Tipo de gobierno' },
  { key: 'entidadFederativa', label: 'Entidad federativa' },
  { key: 'municipio', label: 'Municipio' },
  { key: 'dependencia', label: 'Dependencia' },
  { key: 'puesto', label: 'Puesto/Cargo' },
  { key: 'grado', label: 'Grado' },
  { key: 'departamento', label: 'Departamento' },
  { key: 'carrera', label: 'Carrera' },
  { key: 'cedula', label: 'Cédula' },
  { key: 'efTitulacion', label: 'EF de titulación' },
  { key: 'instEducativa', label: 'Institución educativa' },
  { key: 'cct', label: 'Clave de Centro de Trabajo (CCT)' },
  { key: 'fechaExpCedula', label: 'Fecha de expedición de cédula' },
]

const UNKNOWN_IDS = {
  grado: 1,
  puesto: 2,
  dependencia: 180,
  departamento: 27,
  entidadFederativa: 33,
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

const useMassiveRegister = () => {
  const file = ref(null)
  const excelData = ref([])
  const errors = ref([])
  const toast = useToast()
  const plan = ref(null)
  const periodo = ref(null)
  const { router } = useRouter()
  const isLoading = ref({ submitRequest: false })

  // Convert CSV content to object array.
  const parseCSV = csvText => {
    const lines = csvText
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)

    let startIndex = -1
    let endIndex = -1

    lines.forEach((line, index) => {
      if (line.includes('[START]')) startIndex = index
      if (line.includes('[END]')) endIndex = index
    })

    if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Lectura de CSV fallida',
          text: 'Seleccione un archivo CSV válido',
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })

      return []
    }

    // Fila numérica (1,2,3,4,5,6)
    const numericRow = lines[startIndex]
      .replace('[START]', '')
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)

    // Generar índice → key
    const indexToKey = {}
    numericRow.forEach((num, index) => {
      if (FILE_CSV_COLUMN[num]) {
        indexToKey[index] = FILE_CSV_COLUMN[num]
      }
    })

    // Líneas de datos reales
    const dataLines = lines.slice(startIndex + 1, endIndex)

    return dataLines
      .map(line => line.split(','))
      .filter(cols => cols.some(value => value.trim() !== ''))
      .map(cols => {
        const obj = {}
        Object.entries(indexToKey).forEach(([colIndex, key]) => {
          obj[key] = (cols[colIndex] || '').trim()
        })

        return obj
      })
  }

  // Function to validate if plan and periodo are selected, called before change step 1
  const handleValidationStep1CheckSelectedPlanAndPeriodo = () => {
    if (!plan.value || !(periodo.value && periodo.value.id)) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Comience seleccionando un plan de estudios y periodo',
          text: 'Para continuar, primero seleccione un plan de estudios y un periodo',
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })

      return false
    }

    return true
  }

  // Function to validate if CSV file has been selected, called before change step 2
  const handleValidationStep2CheckSelectedCSVFile = () => {
    if (!excelData.value.length) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Comience cargando un archivo CSV',
          text: 'Para continuar, primero seleccione un archivo CSV',
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })

      return false
    }

    return true
  }

  // Function to verify if dependencies are validated from backend, called before change step 3
  const handleValidationStep3CheckValidatedDependencies = () => {
    const isNotValidated = excelData.value.some(row => !row.wasStringValidated)

    if (isNotValidated) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Validación de datos',
          text: 'Para continuar, primero valide las dependencias ingresadas.',
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })
    }

    return !isNotValidated
  }

  // Function to preload validated dependency data into CSV object data
  const onValidateDependenciesSuccess = data => {
    // 1. Creamos el mapa asegurando que la llave sea un String limpio
    const mapIds = new Map(
      data.map(item => [
        String(item.id).trim(),
        {
          id_dependencia: item.id_dependencia,
          id_entidadFederativaDep: item.id_entidadFederativaDep,
          id_municipio: item.id_municipio,
          id_tipo_gob: item.id_tipo_gob,
          nombre_municipio: item.nombre_municipio,
          nombre_entidadFederativaDep: item.nombre_entidadFederativaDep,
          nombre_dependencia: item.nombre_dependencia,
        },
      ]),
    )

    // 2. Mapeamos el array original con la misma lógica de limpieza
    const resultado = excelData.value.map(obj => {
      // Limpiamos el ID actual para la búsqueda
      const idLimpio = String(obj.curp).trim()
      const dependencia = mapIds.get(idLimpio)

      return {
        ...obj,
        // Buscamos en el mapa y si no existe usamos el valor por defecto
        id_dependencia: dependencia?.id_dependencia ?? null,
        id_entidadFederativaDep: dependencia?.id_entidadFederativaDep ?? null,
        id_municipio: dependencia?.id_municipio ?? null,
        id_tipo_gob: dependencia?.id_tipo_gob ?? null,
        id_direccion: dependencia?.id_direccion ?? null,
        nombre_municipio: dependencia?.nombre_municipio ?? null,
        nombre_entidadFederativaDep: dependencia?.nombre_entidadFederativaDep ?? null,
        nombre_dependencia: dependencia?.nombre_dependencia ?? null,
        wasStringValidated: !!mapIds.get(idLimpio),
      }
    })

    // 3. Actualizamos el estado de excelData con el resultado
    excelData.value = resultado
  }

  // Function to preload validated position data into CSV object data
  const onValidatePositionsSuccess = data => {
    // 1. Creamos el mapa asegurando que la llave sea un String limpio
    const mapIds = new Map(
      data.map(item => [
        String(item.id).trim(),
        {
          id_puesto: item.id_puesto,
          nombre_puesto: item.nombre_puesto,
        },
      ]),
    )

    // 2. Mapeamos el array original con la misma lógica de limpieza
    const resultado = excelData.value.map(obj => {
      // Limpiamos el ID actual para la búsqueda
      const idLimpio = String(obj.curp).trim()
      const puesto = mapIds.get(idLimpio)

      return {
        ...obj,
        // Buscamos en el mapa y si no existe usamos el valor por defecto
        id_puesto: puesto?.id_puesto ?? null,
        nombre_puesto: puesto?.nombre_puesto ?? null,
      }
    })

    // 3. Actualizamos el estado de excelData con el resultado
    excelData.value = resultado
  }

  // Function to preload validated grade data into CSV object data
  const onValidateGradesSuccess = data => {
    // 1. Creamos el mapa asegurando que la llave sea un String limpio
    const mapIds = new Map(
      data.map(item => [
        String(item.id).trim(),
        {
          id_grado: item.id_grado,
          nombre_grado: item.nombre_grado,
        },
      ]),
    )

    // 2. Mapeamos el array original con la misma lógica de limpieza
    const resultado = excelData.value.map(obj => {
      // Limpiamos el ID actual para la búsqueda
      const idLimpio = String(obj.curp).trim()
      const grado = mapIds.get(idLimpio)

      return {
        ...obj,
        // Buscamos en el mapa y si no existe usamos el valor por defecto
        id_grado: grado?.id_grado ?? null,
        nombre_grado: grado?.nombre_grado ?? null,
      }
    })

    // 3. Actualizamos el estado de excelData con el resultado
    excelData.value = resultado
  }

  // Function to preload validated department data into CSV object data
  const onValidateDepartmentsSuccess = data => {
    // 1. Creamos el mapa asegurando que la llave sea un String limpio
    const mapIds = new Map(
      data.map(item => [
        String(item.id).trim(),
        {
          id_departamento: item.id_departamento,
          nombre_departamento: item.nombre_departamento,
        },
      ]),
    )

    // 2. Mapeamos el array original con la misma lógica de limpieza
    const resultado = excelData.value.map(obj => {
      // Limpiamos el ID actual para la búsqueda
      const idLimpio = String(obj.curp).trim()
      const dpto = mapIds.get(idLimpio)

      return {
        ...obj,
        // Buscamos en el mapa y si no existe usamos el valor por defecto
        id_departamento: dpto?.id_departamento ?? null,
        nombre_departamento: dpto?.nombre_departamento ?? null,
      }
    })

    // 3. Actualizamos el estado de excelData con el resultado
    excelData.value = resultado
  }

  // Function to preload validated ef data into CSV object data
  const onValidateEFSuccess = data => {
    // 1. Creamos el mapa asegurando que la llave sea un String limpio
    const mapIds = new Map(
      data.map(item => [
        String(item.id).trim(),
        {
          id_entidadFederativa: item.id_entidadFederativa,
          nombre_entidadFederativa: item.nombre_entidadFederativa,
        },
      ]),
    )

    // 2. Mapeamos el array original con la misma lógica de limpieza
    const resultado = excelData.value.map(obj => {
      // Limpiamos el ID actual para la búsqueda
      const idLimpio = String(obj.curp).trim()
      const dpto = mapIds.get(idLimpio)

      return {
        ...obj,
        // Buscamos en el mapa y si no existe usamos el valor por defecto
        id_entidadFederativa: dpto?.id_entidadFederativa ?? null,
        nombre_entidadFederativa: dpto?.nombre_entidadFederativa ?? null,
      }
    })

    // 3. Actualizamos el estado de excelData con el resultado
    excelData.value = resultado
  }

  // Handle con submit form
  const formSubmitted = async () => {
    // Si no tenemos plan de estudios seleccionado
    if (!plan.value) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Solicitud fallida',
          text: 'Seleccione un plan de estudios válido',
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })

      return
    }

    // Si no tenemos periodo seleccionado
    if (!periodo.value || !periodo.value.id) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Solicitud fallida',
          text: 'Seleccione un periodo válido',
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })

      return
    }

    // Si no tenemos archivo CSV cargado
    if (!excelData.value.length) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Solicitud fallida',
          text: 'Asegúrate de cargar un archivo CSV válido',
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })

      return
    }

    try {
      errors.value = []
      isLoading.value.submitRequest = true

      scrollToTop()

      const body = new FormData()

      body.append('task', 'wsMassiveRegister')
      body.append('origin', process.env.VUE_APP_ORIGIN_API)
      body.append('token', store.state.cuenta.session.token)
      body.append('plan', plan.value)
      body.append('periodo', periodo.value.id)
      body.append('data', JSON.stringify(excelData.value))

      const { data: resquestData } = await postData(body)
      const { status, message, errors: errorsRequest } = resquestData

      if (status !== 200) {
        errors.value = errorsRequest || []

        throw new Error(message)
      }

      toast({
        component: ToastificationContent,
        props: {
          title: 'Solicitud finalizada',
          text: message,
          icon: 'CheckIcon',
          variant: 'success',
        },
      }, { timeout: 8000 })

      // Hacemos redirecto al módulo dashboard una vez finalizado el proceso de registro
      router.push(DASHBOARD_ROUTE)
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Solicitud fallida',
          text: error.message,
          icon: 'XIcon',
          variant: 'danger',
        },
      }, { timeout: 8000 })
    } finally {
      isLoading.value.submitRequest = false
    }
  }

  return {
    file,
    excelData,
    errors,
    toast,
    plan,
    periodo,
    isLoading,

    parseCSV,
    handleValidationStep1CheckSelectedPlanAndPeriodo,
    handleValidationStep2CheckSelectedCSVFile,
    handleValidationStep3CheckValidatedDependencies,

    onValidateDependenciesSuccess,
    onValidatePositionsSuccess,
    onValidateGradesSuccess,
    onValidateDepartmentsSuccess,
    onValidateEFSuccess,

    formSubmitted,
  }
}

export {
  useMassiveRegister,
  TABLE_FIELDS,
  UNKNOWN_IDS,
}
